// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TopNav from "../components/TopNav";
import "../styles/Profile.css";
import bountyPlaceholder from "../assets/bounty-placeholder.svg";
import { fetchPublicUserByUsername, fetchUserAchievements } from "../api/users";
import { getReviews } from "../api/reviews";
import { getTrades } from "../api/trades";
import { getBounties } from "../api/bounties";
import { getUser } from "../auth/auth";
import { isUsableAvatarUrl } from "../utils/avatar";
import ProfileAchievementsSection, {
  getProfileAchievementCardClassName,
} from "../components/ProfileAchievementsSection";

export default function PublicProfile() {
  const navigate = useNavigate();
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [postedTrades, setPostedTrades] = useState([]);
  const [postedBounties, setPostedBounties] = useState([]);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [achievementsError, setAchievementsError] = useState("");
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [postedTab, setPostedTab] = useState("trades");

  useEffect(() => {
    const current = getUser();
    if (current?.username && username && String(current.username).toLowerCase() === String(username).toLowerCase()) {
      navigate("/profile", { replace: true });
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const user = await fetchPublicUserByUsername(username || "");
        setAvatarFailed(false);
        const [reviewData, tradeData, bountyData, achievementsData] = await Promise.all([
          getReviews(user.id),
          getTrades({ userId: user.id }),
          getBounties({ userId: user.id }),
          fetchUserAchievements(user.id).catch((achievementError) => {
            console.error("Failed to fetch achievements:", achievementError);
            setAchievementsError(achievementError?.message || "Could not load achievements.");
            return null;
          }),
        ]);
        setProfile(user);
        setReviews(reviewData || []);
        setPostedTrades(tradeData?.items || []);
        setPostedBounties(bountyData?.items || []);
        setAchievements(achievementsData);
        if (achievementsData) {
          setAchievementsError("");
        }
      } catch (err) {
        setError(err?.message || "Profile not found.");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [username, navigate]);

  const initials = useMemo(() => {
    if (!profile?.name) return "CU";
    return String(profile.name)
      .split(" ")
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [profile?.name]);

  const renderStars = (rating) => {
    const fullStars = Math.floor(Number(rating) || 0);
    const stars = [];
    for (let i = 0; i < 5; i += 1) {
      stars.push(
        <span key={i} style={{ color: i < fullStars ? "#7cf3ff" : "#444" }}>
          ★
        </span>,
      );
    }
    return stars;
  };

  const renderReviewDetails = (review) => {
    const tags = Array.isArray(review?.tags) ? review.tags : [];
    const hasComment = Boolean(String(review?.comment || "").trim());

    return (
      <>
        {review?.autoGenerated && <div className="review-badge">Auto-finalized after 48h</div>}
        {tags.length > 0 && (
          <div className="review-tags">
            {tags.map((tag) => (
              <span key={tag} className="review-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        {hasComment && <div className="review-comment">{review.comment}</div>}
      </>
    );
  };

  const achievementsSection = (
    <ProfileAchievementsSection
      achievements={achievements}
      loading={loading}
      error={!loading ? achievementsError : ""}
    />
  );

  return (
    <>
      <TopNav />
      <div className="profile-screen page-with-top-nav">
        <div
          className={[
            "profile-card",
            getProfileAchievementCardClassName(achievements?.display?.profileBorderTone || "none"),
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <header className="profile-header">
            <button className="back" type="button" onClick={() => navigate(-1)}>
              ←
            </button>
            <div>
              <h1>Trader Profile</h1>
              <p>Public profile</p>
            </div>
          </header>

          {loading ? (
            <div className="loading-state">Loading profile...</div>
          ) : error ? (
            <div className="empty-state">{error}</div>
          ) : (
            <>
              <div className="profile-body">
                <div className="profile-avatar" aria-hidden="true">
                  {isUsableAvatarUrl(profile?.avatar) && !avatarFailed ? (
                    <img
                      src={profile.avatar}
                      alt="Profile avatar"
                      className="avatar-image"
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <span className="avatar-initials">{initials}</span>
                  )}
                </div>
                <div>
                  <h2>{profile?.name || "Cardex Trader"}</h2>
                  <p className="profile-subtitle">
                    @{profile?.username || "unknown"} · Member since{" "}
                    {new Date(profile?.createdAt || Date.now()).getFullYear()}
                  </p>
                </div>
              </div>

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Trust Score</div>
                  <div className="stat-value">{profile?.trustScore || "neutral"}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Reviews</div>
                  <div className="stat-value">{Number(profile?.reviewCount || 0)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Items Listed</div>
                  <div className="stat-value">{Number(profile?.itemsListed || 0)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Sales</div>
                  <div className="stat-value">{Number(profile?.salesCount || 0)}</div>
                </div>
              </div>

              <div className="profile-tabs" role="tablist" aria-label="Profile tabs">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "overview"}
                  className={`profile-tab ${activeTab === "overview" ? "active" : ""}`}
                  onClick={() => setActiveTab("overview")}
                >
                  Overview
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "reviews"}
                  className={`profile-tab ${activeTab === "reviews" ? "active" : ""}`}
                  onClick={() => setActiveTab("reviews")}
                >
                  Reviews
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "posted"}
                  className={`profile-tab ${activeTab === "posted" ? "active" : ""}`}
                  onClick={() => setActiveTab("posted")}
                >
                  Posted
                </button>
              </div>

              {activeTab === "overview" && (
                <>
                  <div className="reviews-section">
                    <h3 className="section-title">Recent Reviews</h3>
                    {reviews.length === 0 ? (
                      <div className="empty-state">No reviews yet</div>
                    ) : (
                      <div className="reviews-list">
                        {reviews.slice(0, 3).map((review, index) => (
                          <div key={review?.id || `review-${index}`} className="review-card">
                            <div className="review-header">
                              <div>
                                <div className="reviewer-name">{review?.reviewerName || "Trader"}</div>
                                <div className="review-date">
                                  {review?.date
                                    ? new Date(review.date).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                    : ""}
                                </div>
                              </div>
                              <div className="review-rating">{renderStars(review?.rating)}</div>
                            </div>
                            {renderReviewDetails(review)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {achievementsSection}
                </>
              )}

              {activeTab === "reviews" && (
                <>
                  <div className="reviews-section">
                    <h3 className="section-title">All Reviews ({reviews.length})</h3>
                    {reviews.length === 0 ? (
                      <div className="empty-state">No reviews yet</div>
                    ) : (
                      <div className="reviews-list">
                        {reviews.map((review, index) => (
                          <div key={review?.id || `review-full-${index}`} className="review-card">
                            <div className="review-header">
                              <div className="reviewer-name">{review?.reviewerName || "Trader"}</div>
                              <div className="review-rating">{renderStars(review?.rating)}</div>
                            </div>
                            {renderReviewDetails(review)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {achievementsSection}
                </>
              )}

              {activeTab === "posted" && (
                <div className="posted-section">
                  <div className="posted-subtabs" role="tablist" aria-label="Posted listing tabs">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={postedTab === "trades"}
                      className={`posted-subtab ${postedTab === "trades" ? "active" : ""}`}
                      onClick={() => setPostedTab("trades")}
                    >
                      Trades
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={postedTab === "bounties"}
                      className={`posted-subtab ${postedTab === "bounties" ? "active" : ""}`}
                      onClick={() => setPostedTab("bounties")}
                    >
                      Bounties
                    </button>
                  </div>

                  {postedTab === "trades" &&
                    (postedTrades.length === 0 ? (
                      <div className="empty-state">No posted trades.</div>
                    ) : (
                      <div className="posted-list">
                        {postedTrades.map((trade) => (
                          <div
                            key={trade.id}
                            className="posted-card posted-card--trade"
                            onClick={() => navigate(`/trade-offer/${trade.id}`)}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="posted-card-title">{trade.cardName}</div>
                            <div className="posted-card-meta">
                              {trade.game} · {trade.condition || "Any condition"}
                            </div>
                            <div className="posted-card-meta">
                              Postal {trade.zipCode || "N/A"} · {trade.radius || 25} mi
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}

                  {postedTab === "bounties" &&
                    (postedBounties.length === 0 ? (
                      <div className="empty-state">No posted bounties.</div>
                    ) : (
                      <div className="posted-list">
                        {postedBounties.map((bounty) => (
                          <div
                            key={bounty.id}
                            className="posted-card posted-bounty-card"
                            onClick={() => navigate(`/bounty-offer/${bounty.id}`)}
                            role="button"
                            tabIndex={0}
                          >
                            <img
                              src={bounty.photoUrl || bountyPlaceholder}
                              alt={bounty.cardName}
                              className="posted-bounty-image"
                              onError={(e) => {
                                e.currentTarget.src = bountyPlaceholder;
                              }}
                            />
                            <div className="posted-card-content">
                              <div className="posted-card-title">{bounty.cardName}</div>
                              <div className="posted-card-meta">
                                {bounty.game} · {bounty.desiredCondition || "Any condition"}
                              </div>
                              <div className="posted-card-meta">
                                Postal {bounty.zipCode || "N/A"} · {bounty.radius || 25} mi
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
